/**
 * TableShell — primitive layout building blocks for grid-style tables.
 *
 * We're not using <table> here because dashboards lean on grid-aligned rows
 * with mixed cell widths and embedded controls. This wrapper gives us the
 * Aurora chrome and the row-divider behavior — TanStack Table will be wired
 * on top of it for sorting/paging in a follow-up.
 *
 * Composition:
 *   <TableShell>
 *     <TableHeadRow columns={cols}>
 *       <TableHeadCell>Site</TableHeadCell> ...
 *     </TableHeadRow>
 *     <TableRow columns={cols}>...</TableRow>
 *   </TableShell>
 */
import { type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

export type TableShellProps = HTMLAttributes<HTMLDivElement>;

export function TableShell({ className, ...props }: TableShellProps) {
  const animatedRowsRef = useUiAutoAnimate();

  return (
    <div
      ref={animatedRowsRef}
      className={cn(
        "rounded-2xl border border-line bg-surface overflow-hidden",
        className,
      )}
      {...props}
    />
  );
}

interface RowProps extends HTMLAttributes<HTMLDivElement> {
  /** Grid columns spec, e.g. "1.2fr 1.4fr 0.8fr 0.7fr". */
  columns: string;
  children?: ReactNode;
}

export function TableRow({
  columns,
  className,
  children,
  style,
  ...props
}: RowProps) {
  return (
    <div
      className={cn(
        "grid items-center gap-3 px-4 py-3 text-sm border-t border-line/60 first:border-t-0",
        className,
      )}
      style={{ gridTemplateColumns: columns, ...(style as CSSProperties) }}
      {...props}
    >
      {children}
    </div>
  );
}

export function TableHeadRow({
  columns,
  className,
  children,
  style,
  ...props
}: RowProps) {
  return (
    <div
      className={cn(
        "grid items-center gap-3 px-4 py-2.5",
        "text-[10px] font-mono uppercase tracking-[0.1em] text-fg-mute",
        "bg-surface-tint border-b border-line",
        className,
      )}
      style={{ gridTemplateColumns: columns, ...(style as CSSProperties) }}
      {...props}
    >
      {children}
    </div>
  );
}

export function TableHeadCell({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("min-w-0", className)} {...props} />;
}

export function TableCell({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("min-w-0", className)} {...props} />;
}
